import { AuthenticationOidcProvider, AutoLogout, ScrollToTopOnNavigation, Spinner } from '@cp-shared-19/frontend-ui';
import {
    changeAddressPath,
    changeBillingAddressPath,
    changeContactDetailsPath,
    changeIbanPath,
    changeMarketingSettingsPath,
    civicToolCookiePolicyPath,
    contactDetailsCheckPagePath,
    contractEndOptionsPath,
    cookiePolicyPath,
    dashboardPagePath,
    earlySettlementPagePath,
    faqPagePath,
    forbiddenPagePath,
    landingPagePath,
    legalNoticePath,
    myProfilePagePath,
    myRequestsPagePath,
    myRequestsPagePathForContact,
    notAuthorizedPagePath,
    notFoundPagePath,
    partialEarlySettlementPagePath,
    privacyPolicyPath,
    registrationPagePath,
    totalEarlySettlementPagePath,
    parkingPagePath,
    earlyReturnPenaltyPagePath,
    parkingLinkingResultPath,
    myDocumentsPath,
    confirmInformationPath,
    loginRedirectPagePath,
    errorPagePath,
    logoutPagePath,
    requestHistoryPagePath,
} from 'components/navigation/paths';
import { ConnectedRouter } from 'connected-react-router';
import {
    ContractEndOptionsPage,
    CookiePolicyPage,
    DashboardPage,
    EarlySettlementPage,
    FaqPage,
    ForbiddenPage,
    LandingPage,
    LegalNoticePage,
    MasterPage,
    MyProfilePage,
    NotAuthorizedPage,
    NotFoundPage,
    RegistrationPage,
    RequestPage,
    ParkingPage,
    EarlyReturnPenaltyPage,
    ErrorPage,
} from 'pages';
import { ContactDetailsCheckPage } from 'pages/contact-details-check-page';
import { PartialEarlySettlementPage } from 'pages/partial-early-settlement-page/PartialEarlySettlementPage';
import { TotalEarlySettlementPage } from 'pages/total-early-settlement-page/TotalEarlySettlementPage';
import React, { Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { AuthenticatedRoute, AuthorizedRoute, UserRegistrationProvider } from './auth';
import { LastLocationProvider } from 'react-router-last-location';
import { AxiosConfiguration } from './auth/AxiosConfiguration';
import { PrivacyPolicyPage } from './pages/privacy-policy-page';
import { history } from './store/rootReducer';
import { ParkingLinkingResult } from './components/parking/parking-linking-result/ParkingLinkingResult';
import { payByPhoneEnabled } from './config';
import { MyDocumentsPage } from 'pages/my-documents-page';
import { RequestHistoryPage } from 'pages/request-history-page';
import { ConfirmInformationPage } from 'pages/confirm-information-page/ConfirmInformationPage';
import { LoginRedirect } from 'pages/login-redirect/LoginRedirect';
import { useTranslation } from 'react-i18next';
import { AutoLogoutPage } from './components/error-pages/auto-logout/AutoLogoutPage';
import { SkipLink } from './components/skip-link';
import 'localization/i18n';

const App: React.FC = () => {
    const { t } = useTranslation('loader');

    return (
        <div className="App">
            <Suspense
                fallback={
                    <Spinner center>
                        <p>{t('genericText')}</p>
                    </Spinner>
                }
            >
                <AuthenticationOidcProvider>
                    <UserRegistrationProvider>
                        <AutoLogout oidc idleTimeInMinutes={10} redirectUri={window.location.origin + logoutPagePath()}>
                            <AxiosConfiguration>
                                <ConnectedRouter history={history}>
                                    <ScrollToTopOnNavigation
                                        exceptions={[
                                            { from: /\S*/, to: new RegExp(changeIbanPath('[\\w-]+')) },
                                            { from: new RegExp(changeIbanPath('[\\w-]+')), to: dashboardPagePath() },
                                            { from: myProfilePagePath(), to: changeContactDetailsPath() },
                                            { from: changeContactDetailsPath(), to: myProfilePagePath() },
                                            { from: myProfilePagePath(), to: changeAddressPath() },
                                            { from: changeAddressPath(), to: myProfilePagePath() },
                                            { from: myProfilePagePath(), to: changeBillingAddressPath() },
                                            { from: changeBillingAddressPath(), to: myProfilePagePath() },
                                            { from: changeMarketingSettingsPath(), to: myProfilePagePath() },
                                            { from: myProfilePagePath(), to: changeMarketingSettingsPath() },
                                        ]}
                                    />
                                    <LastLocationProvider>
                                        <SkipLink />
                                        <MasterPage>
                                            <Switch>
                                                <Route exact path={landingPagePath()}>
                                                    <LandingPage />
                                                </Route>
                                                <Route path={logoutPagePath()}>
                                                    <AutoLogoutPage />
                                                </Route>
                                                <Route path={loginRedirectPagePath()}>
                                                    <LoginRedirect />
                                                </Route>
                                                <Route path={faqPagePath()}>
                                                    <FaqPage />
                                                </Route>
                                                <Route path={cookiePolicyPath()}>
                                                    <CookiePolicyPage />
                                                </Route>
                                                <Route path={civicToolCookiePolicyPath()}>
                                                    <Redirect to={cookiePolicyPath()} />
                                                </Route>
                                                <Route path={legalNoticePath()}>
                                                    <LegalNoticePage />
                                                </Route>
                                                <Route path={privacyPolicyPath()}>
                                                    <PrivacyPolicyPage />
                                                </Route>
                                                <AuthorizedRoute path={dashboardPagePath()}>
                                                    <DashboardPage />
                                                </AuthorizedRoute>
                                                <AuthorizedRoute path={requestHistoryPagePath()}>
                                                    <RequestHistoryPage />
                                                </AuthorizedRoute>
                                                <AuthorizedRoute path={myDocumentsPath()}>
                                                    <MyDocumentsPage />
                                                </AuthorizedRoute>
                                                <AuthorizedRoute path={myProfilePagePath()}>
                                                    <MyProfilePage />
                                                </AuthorizedRoute>
                                                <AuthorizedRoute path={myRequestsPagePath()}>
                                                    <RequestPage />
                                                </AuthorizedRoute>
                                                {payByPhoneEnabled && (
                                                    <AuthorizedRoute path={parkingPagePath()}>
                                                        <ParkingPage />
                                                    </AuthorizedRoute>
                                                )}
                                                {payByPhoneEnabled && (
                                                    <AuthorizedRoute path={parkingLinkingResultPath()}>
                                                        <ParkingLinkingResult />
                                                    </AuthorizedRoute>
                                                )}
                                                <AuthorizedRoute path={myRequestsPagePathForContact()}>
                                                    <RequestPage />
                                                </AuthorizedRoute>
                                                <AuthenticatedRoute path={registrationPagePath()}>
                                                    <RegistrationPage />
                                                </AuthenticatedRoute>
                                                <AuthenticatedRoute path={contactDetailsCheckPagePath()}>
                                                    <ContactDetailsCheckPage />
                                                </AuthenticatedRoute>
                                                <AuthenticatedRoute path={earlySettlementPagePath()}>
                                                    <EarlySettlementPage />
                                                </AuthenticatedRoute>
                                                <AuthenticatedRoute path={earlyReturnPenaltyPagePath()}>
                                                    <EarlyReturnPenaltyPage />
                                                </AuthenticatedRoute>
                                                <AuthenticatedRoute path={partialEarlySettlementPagePath()}>
                                                    <PartialEarlySettlementPage />
                                                </AuthenticatedRoute>
                                                <AuthenticatedRoute path={totalEarlySettlementPagePath()}>
                                                    <TotalEarlySettlementPage />
                                                </AuthenticatedRoute>
                                                <AuthenticatedRoute path={contractEndOptionsPath()}>
                                                    <ContractEndOptionsPage />
                                                </AuthenticatedRoute>
                                                <AuthorizedRoute path={confirmInformationPath()}>
                                                    <ConfirmInformationPage />
                                                </AuthorizedRoute>
                                                <Route path={forbiddenPagePath()}>
                                                    <ForbiddenPage />
                                                </Route>
                                                <Route path={notAuthorizedPagePath()}>
                                                    <NotAuthorizedPage />
                                                </Route>
                                                <Route path={errorPagePath()}>
                                                    <ErrorPage />
                                                </Route>
                                                <Route path={notFoundPagePath()}>
                                                    <NotFoundPage />
                                                </Route>
                                                <Route path="*">
                                                    <Redirect to={notFoundPagePath()} />
                                                </Route>
                                            </Switch>
                                        </MasterPage>
                                    </LastLocationProvider>
                                </ConnectedRouter>
                            </AxiosConfiguration>
                        </AutoLogout>
                    </UserRegistrationProvider>
                </AuthenticationOidcProvider>
            </Suspense>
        </div>
    );
};

export default App;
